import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  ClaimProfile,
  FilingPackageItem,
  SelectedSettlement,
  SettlementListItem,
  SignedAuthorization,
} from './types';
import { EMPTY_PROFILE } from './domain/profile';
import { toSelectedSettlement } from './domain/payout';

const DEFAULT_SELECT_COUNT = 3;

interface ClaimsState {
  selected: SelectedSettlement[];
  defaultApplied: boolean;
  profile: ClaimProfile;
  packages: FilingPackageItem[];
  signedAuthorizations: SignedAuthorization[];
  clientSubmissionId: string;
  selectDefaults: (items: SettlementListItem[]) => void;
  toggleSelected: (item: SelectedSettlement) => void;
  removeSelected: (id: string) => void;
  setProfile: (patch: Partial<ClaimProfile>) => void;
  setPackages: (packages: FilingPackageItem[]) => void;
  upsertSigned: (auth: SignedAuthorization) => void;
  markAuthorizationsUploaded: (ids: string[]) => void;
  clearSignedAuthorizations: () => void;
  reset: () => void;
}

function newSubmissionId(): string {
  return crypto.randomUUID();
}

const initial = {
  selected: [] as SelectedSettlement[],
  defaultApplied: false,
  profile: EMPTY_PROFILE,
  packages: [] as FilingPackageItem[],
  signedAuthorizations: [] as SignedAuthorization[],
};

export const useClaimsStore = create<ClaimsState>()(
  persist(
    (set, get) => ({
      ...initial,
      clientSubmissionId: newSubmissionId(),
      selectDefaults: (items) => {
        if (get().defaultApplied) return;
        const allHaveFlag = items.every((item) => item.selected_by_default != null);
        if (allHaveFlag) {
          set({
            selected: items.filter((item) => item.selected_by_default).map(toSelectedSettlement),
            defaultApplied: true,
          });
          return;
        }
        if (get().selected.length > 0) return;
        set({
          selected: items.slice(0, DEFAULT_SELECT_COUNT).map(toSelectedSettlement),
          defaultApplied: true,
        });
      },
      toggleSelected: (item) => {
        set((s) => {
          const exists = s.selected.some((c) => c.id === item.id);
          return {
            defaultApplied: true,
            selected: exists
              ? s.selected.filter((c) => c.id !== item.id)
              : [...s.selected, item],
            signedAuthorizations: exists
              ? s.signedAuthorizations.filter((a) => a.settlement_id !== item.id)
              : s.signedAuthorizations,
          };
        });
      },
      removeSelected: (id) => {
        set((s) => ({
          selected: s.selected.filter((c) => c.id !== id),
          signedAuthorizations: s.signedAuthorizations.filter((a) => a.settlement_id !== id),
        }));
      },
      setProfile: (patch) => {
        set((s) => ({ profile: { ...s.profile, ...patch } }));
      },
      setPackages: (packages) => set({ packages }),
      upsertSigned: (auth) => {
        set((s) => ({
          signedAuthorizations: [
            ...s.signedAuthorizations.filter((a) => a.settlement_id !== auth.settlement_id),
            auth,
          ],
        }));
      },
      markAuthorizationsUploaded: (ids) => {
        const uploaded = new Set(ids);
        set((s) => {
          const signedAuthorizations = s.signedAuthorizations.filter(
            (item) => !uploaded.has(item.settlement_id),
          );
          if (signedAuthorizations.length > 0) return { signedAuthorizations };
          return {
            signedAuthorizations: [],
            packages: [],
            clientSubmissionId: newSubmissionId(),
          };
        });
      },
      clearSignedAuthorizations: () =>
        set({
          signedAuthorizations: [],
          packages: [],
          clientSubmissionId: newSubmissionId(),
        }),
      reset: () =>
        set({
          ...initial,
          profile: EMPTY_PROFILE,
          clientSubmissionId: newSubmissionId(),
        }),
    }),
    {
      name: 'mf-claims',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
